<template>
  <div>
    <div class="flex flex-wrap w-full items-center justify-center" v-if="mfaStep === 1">
      <div class="flex flex-wrap w-full items-center justify-center" v-if="enforced">
        <div class="flex-inline flex-wrap w-full items-center text-left hireful-font-domus text-gray-400 py-4">
          One or more of the systems you’re assigned to requires <strong>multi-factor authentication (MFA)</strong> to be enabled before you can login.
        </div>
        <div class="flex-inline flex-wrap w-full items-center text-left hireful-font-domus text-gray-400 py-4">
          <strong>MFA</strong> adds an extra layer of security to your account by sending a code to your phone which will be used in the login process.
        </div>
        <div class="flex-inline w-full items-center text-left hireful-font-domus text-gray-400 py-4">
          Please enter your mobile number in the field below, we will then send you a code to enable <strong>MFA</strong>.
        </div>
      </div>
      <div class="flex flex-wrap w-full items-center justify-start" v-if="!enforced">
        <div class="flex-inline w-full items-center text-left hireful-font-domus text-gray-400 py-4">
          To add <strong>multi-factor authentication (MFA)</strong> to your account we require your mobile phone number.
        </div>
        <div class="flex-inline w-full items-center text-left hireful-font-domus text-gray-400 py-4">
          Please enter your mobile number in the field below, we will then send you a code to enable <strong>MFA</strong>.
        </div>
      </div>
      <div class="flex flex-wrap w-full items-center justify-center">
        <div class="flex w-full items-stretch justify-start">
          <div class="flex w-1/6 relative items-end justify-center mx-2">
            <text-field v-model="countryCode" @clicked="toggleCountryListVisibility()" />
            <div class="absolute right-0 top-20 w-80 h-80 overflow-auto bg-gray-100 shadow-xl" v-if="showCountryList" v-click-outside="onClickOutside">
              <div class="p-2 border-b cursor-pointer" v-for="(c, i) in countryCodeWithNameList" :key="`country-code-${i}`" @click="selectCountry(c)">
                {{c.label}}
              </div>
            </div>
          </div>
          <div class="flex flex-wrap w-4/6">
            <div class="flex w-full text-gray-400 text-sm py-2 hireful-font-domus">
              Mobile phone number
              <span class="text-hireful-yellow"> *</span>
            </div>
            <div class="flex w-full">
              <text-field v-model="phone" @enter="sendCode()" />
            </div>
          </div>
          <div class="flex w-1/6 items-end justify-center">
            <div class="tooltip">
              <hireful-icon icon="info" class="ml-2 h-6 w-6"></hireful-icon>
              <span class="tooltiptext tooltiptop hireful-font-domus">
                  Please make sure you remove the first digit of your mobile phone
                number in line with international dialling standards
                e.g. for +44
                  region numbers, please remove the “0” digit at the start of your number.
              </span>
            </div>
          </div>
        </div>
      </div>
      <div class="flex flex-wrap w-full item-center justify-center mt-5" v-if="showNotification">
        <hireful-notification :msg="notificationMessage"></hireful-notification>
      </div>
      <div class="flex w-full items-center justify-center py-4">
        <loader v-if="loading" />
        <div class="flex flex-wrap w-full items-center justify-center" v-else>
          <button class="bg-hireful text-white w-full hireful-font-semibold h-12 rounded-lg" @click="sendCode()" >
            Send code
          </button>
          <div class="flex w-full items-center justify-center py-4">
            <span class="hireful-font-semibold text-gray-400 cursor-pointer" @click="back()" v-if="!enforced">
              Go back
            </span>
          </div>
        </div>
      </div>
    </div>
    <div class="flex flex-wrap w-full items-center justify-center" v-if="mfaStep === 2">
      <div class="flex flex-wrap w-full items-center justify-center">
        <div class="flex w-full items-center text-left hireful-font-domus text-gray-400 py-4">
          We have sent a text message with a sign-in code to the mobile number ending in xxxxxxx-{{maskedPhoneNumber}}.
        </div>
        <div class="flex w-full items-center text-left hireful-font-domus text-gray-400 py-4">
          Please enter the received code in the field below. Please note: it may take a couple of minutes for the text message to reach you.
        </div>
        <div class="flex flex-wrap w-full items-center justify-center text-left hireful-font-domus text-gray-400 py-4">
          If you are having issues receiving your code please get in touch with <a class="contents text-hireful-link" href="mailto:support@hireful.co.uk">support@hireful.co.uk</a>
        </div>
      </div>
      <div class="flex flex-wrap w-full">
        <div class="flex flex-wrap w-full items-center justify-center py-4">
          <div class="flex w-full text-gray-400 text-sm py-2 hireful-font-domus">
            Sign in code
            <span class="text-hireful-yellow"> *</span>
          </div>
          <div class="flex w-full">
            <text-field v-model="signInCode" @enter="submitCode()" />
          </div>
        </div>
        <div class="flex w-full items-center justify-start">
          <div class="hireful-font-domus text-gray-400 cursor-pointer" @click="sendCode">
            Code not received? click to resend
          </div>
        </div>
      </div>
      <div class="flex flex-wrap w-full item-center justify-center py-4" v-if="showNotification">
        <hireful-notification :msg="notificationMessage"></hireful-notification>
      </div>
      <div class="flex w-full items-center justify-center py-4">
        <loader v-if="loading" />
        <div class="flex flex-wrap w-full items-center justify-center" v-else>
          <button class="bg-hireful text-white w-full hireful-font-semibold h-12 rounded-lg" @click="submitCode()" >
            Submit
          </button>
          <div class="flex w-full items-center justify-center py-4">
            <span class="hireful-font-semibold text-gray-400 cursor-pointer" @click="back()" v-if="!enforced">
              Go back
            </span>
          </div>
        </div>
      </div>
    </div>
    <div class="flex flex-wrap w-full items-center justify-center" v-if="mfaStep === 3">
      <div class="flex flex-wrap w-full items-center justify-center">
        <div class="flex-inline w-full items-center text-left hireful-font-domus text-gray-400 py-4">
          <strong>Multi-factor authentication (MFA)</strong> has now been enabled on your account.
        </div>
        <div class="flex w-full items-center text-left hireful-font-domus text-gray-400 py-4">
          When you log-in to the system in the future you may be prompted to enter another code sent to your mobile phone.
        </div>
        <div class="flex-inline w-full items-center text-left hireful-font-domus text-gray-400 py-4">
          You can change your <strong>MFA</strong> settings by visiting your <strong>personal settings</strong> once you have logged into the system.
        </div>
      </div>
      <div class="flex w-full items-center justify-center py-4">
        <loader v-if="loading" />
        <div class="flex flex-wrap w-full items-center justify-center" v-else>
          <button class="bg-hireful text-white w-full hireful-font-semibold h-12 rounded-lg" @click="continueLogin()" >
            Continue
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { cloneDeep } from 'lodash'

import axios from 'axios'
import loader from '@/components/loader'
import hirefulIcon from '@/components/hireful-icon'
import store from '@/store'
import countryCodes from '@/assets/json/countryCodes'
import vClickOutside from 'v-click-outside'
import hirefulNotification from '@/components/hireful-notification'
import textField from '@/components/textField'

export default {
  name: 'setupMfa',
  props: {
    enforced: {
      type: Boolean,
      default: false
    }
  },
  directives: {
    clickOutside: vClickOutside.directive
  },
  components: {
    loader,
    hirefulIcon,
    hirefulNotification,
    textField
  },
  data () {
    return {
      loading: false,
      phone: '',
      verifyPhoneNumberResp: null,
      defaultCountryCodeIso: 'GB',
      countryCode: null,
      showCountryList: false,
      mfaStep: 1,
      signInCode: null,
      showNotification: false,
      notificationMessage: ''
    }
  },
  computed: {
    countryCodeWithNameList () {
      const countryWithCodeOptions = []
      for (const code in countryCodes) {
        const option = { label: `${countryCodes[code].name} (${countryCodes[code].code})`, value: countryCodes[code].code }
        countryWithCodeOptions.push(option)
      }
      countryWithCodeOptions.sort((a, b) => a.label.localeCompare(b.label))
      return countryWithCodeOptions
    },
    maskedPhoneNumber () {
      return `${this.phone.toString().slice(-4)}`
    }
  },
  mounted () {
    const vm = this
    vm.$emit('setPageTitle', 'Set up multi-factor authentication')
    vm.setDefaultCountryCode()
  },
  methods: {
    async sendCode () {
      const vm = this
      vm.clearNotification()
      let isValid = true
      vm.showNotification = false
      const mNumber = cloneDeep(vm.phone)
      if (vm.countryCode && vm.countryCode === 44) {
        if (vm.phone && String(mNumber).charAt(0) === '0') {
          isValid = false
          vm.showNotification = true
          vm.notificationMessage = 'Please remove the first “0” digit from your mobile phone number. This digit is not required when using the +44 region code.'
        }
      }
      if (isValid) {
        vm.loading = true

        const payload = { phone_number: `+${vm.countryCode}${vm.phone}` }

        const verifyPhoneNumberResp = await axios.post(`${process.env.VUE_APP_LOGIN_API_HOST}${store.state.routes.verifyPhoneNumber}`, payload)
        if (verifyPhoneNumberResp.status === 200 && verifyPhoneNumberResp.data.success) {
          vm.prepareSecondStep(verifyPhoneNumberResp.data)
        } else {
          vm.showNotification = true
          vm.notificationMessage = 'Something went wrong while sending the code. Please check the number and try again.'
          vm.loading = false
        }
      }
    },
    prepareSecondStep (data) {
      const vm = this
      vm.verifyPhoneNumberResp = data
      vm.mfaStep = 2
      vm.$emit('setPageTitle', `Code sent to xxxxxxx-${vm.maskedPhoneNumber}`)
      vm.loading = false
    },
    prepareThirdStep () {
      const vm = this
      vm.mfaStep = 3
      vm.$emit('setPageTitle', 'Multi-factor authentication has been enabled')
      vm.loading = false
    },
    back () {
      const vm = this
      if (vm.mfaStep === 1) {
        vm.$emit('back', true)
      } else if (vm.mfaStep === 2) {
        vm.resetMfaSetup()
      }
    },
    resetMfaSetup () {
      const vm = this
      vm.phone = ''
      vm.setDefaultCountryCode()
      vm.mfaStep = 1
      vm.loading = false
      vm.$emit('setPageTitle', 'Set up multi-factor authentication')
    },
    setDefaultCountryCode () {
      const vm = this
      for (const code in countryCodes) {
        if (countryCodes[code].iso.toLowerCase() === vm.defaultCountryCodeIso.toLowerCase()) {
          vm.countryCode = countryCodes[code].code
        }
      }
    },
    toggleCountryListVisibility () {
      const vm = this
      vm.showCountryList = !vm.showCountryList
    },
    selectCountry (option) {
      const vm = this
      vm.countryCode = option.value
      vm.showCountryList = false
    },
    onClickOutside () {
      const vm = this
      vm.showCountryList = false
    },
    async submitCode () {
      const vm = this
      vm.loading = true
      vm.clearNotification()
      if (vm.signInCode) {
        const payload = {
          otp: vm.signInCode,
          phone_number: `+${vm.countryCode}${vm.phone}`,
          secret_token: vm.verifyPhoneNumberResp.secret_token
        }

        const verifySignInCodeResp = await axios.post(`${process.env.VUE_APP_LOGIN_API_HOST}${store.state.routes.verifySignInCode}`, payload)
        if (verifySignInCodeResp.status === 200 && verifySignInCodeResp.data) {
          vm.prepareThirdStep()
        } else {
          vm.showNotification = true
          vm.notificationMessage = 'Invalid code. Please try again.'
          vm.loading = false
        }
      } else {
        vm.showNotification = true
        vm.notificationMessage = 'Please enter the sign in code.'
        vm.loading = false
      }
    },
    continueLogin () {
      const vm = this
      vm.$emit('continue', true)
    },
    clearNotification () {
      const vm = this
      vm.showNotification = false
      vm.notificationMessage = ''
    }
  }
}
</script>

<style>

</style>
